import { useState, ReactNode, useEffect } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption,
} from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/16/solid';
import { CheckIcon } from '@heroicons/react/20/solid';
import { DropdownOption } from '@employ-engage/data';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

export interface MultiselectDropdownProps {
  id: string;
  options: any[];
  values: any[];
  onChange: (selected: any[]) => void;
  label?: string;
  isDisabled?: boolean;
  includeNoneOption?: boolean;
  mapPayload: (item: any) => DropdownOption;
  className?: string;
  staticMenuItems?: { id: string; label: ReactNode; onClick: () => void }[];
}

export const MultiselectDropdown = ({
  id,
  options,
  values = [],
  onChange,
  label = 'Select...',
  isDisabled = false,
  includeNoneOption = false,
  mapPayload,
  className,
  staticMenuItems = [],
}: MultiselectDropdownProps) => {
  const [mappedOptions, setMappedOptions] = useState(
    options?.map?.(mapPayload) || []
  );
  const [selectedOptions, setSelectedOptions] = useState(
    values?.map?.(mapPayload) || []
  );

  useEffect(() => {
    setMappedOptions(options?.map?.(mapPayload) || []);
  }, [options, mapPayload]);

  useEffect(() => {
    setSelectedOptions(values?.map?.(mapPayload) || []);
  }, [values, mapPayload]);

  // Handle selection changes from Listbox
  const handleChange = (selected: DropdownOption[]) => {
    // If "None" is selected, clear all
    if (selected.some((opt) => opt.id === 'none')) {
      setSelectedOptions([]);
      onChange([]);
      return;
    }
    setSelectedOptions(selected);
    onChange(selected.map((item) => item.payload));
  };

  // Compose options, including "None" if needed
  const allOptions = includeNoneOption
    ? [{ id: 'none', label: 'None', payload: null }, ...mappedOptions]
    : mappedOptions;

  return (
    <div key={id} className={className}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <Listbox
        value={selectedOptions}
        onChange={handleChange}
        multiple
        disabled={isDisabled}
        as="div"
      >
        <div className="relative">
          <ListboxButton
            id={id}
            className="w-full rounded-md border px-3 py-2 text-left mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            aria-label={label}
          >
            <span>
              {selectedOptions.length === 0
                ? 'Select...'
                : selectedOptions.map((v) => v.label).join(', ')}
            </span>
            <ChevronUpDownIcon className="w-5 h-5 absolute right-2 top-2 pointer-events-none" />
          </ListboxButton>
          <ListboxOptions
            className="absolute z-10 mt-1 w-full bg-white border rounded shadow max-h-60 overflow-auto"
            aria-labelledby={id}
          >
            {staticMenuItems.map((item) => (
              <button
                key={item.id}
                className="w-full text-left cursor-pointer select-none relative py-2 pl-8 pr-4 hover:bg-light-primary hover:text-white"
                onClick={item.onClick}
              >
                {item.label} <FontAwesomeIcon icon={faPlus} className="ml-2" />
              </button>
            ))}
            <div className="border-t" />
            {allOptions.map((option) => (
              <ListboxOption
                key={option.id}
                value={option}
                as="div"
                className={({ active }) =>
                  `cursor-pointer select-none relative py-2 pl-8 pr-4 ${
                    active ? 'bg-light-primary text-white' : ''
                  }`
                }
              >
                {({ selected, active }) => (
                  <>
                    <span
                      className={`block truncate ${
                        selected ? 'font-semibold' : 'font-normal'
                      }`}
                    >
                      {option.label}
                    </span>
                    {(selected ||
                      (includeNoneOption &&
                        option.id === 'none' &&
                        selectedOptions.length === 0)) && (
                      <span
                        className={classNames(
                          'absolute inset-y-0 left-0 flex items-center pl-1.5',
                          {
                            'text-white': active,
                            'text-light-primary': !active,
                          }
                        )}
                      >
                        <CheckIcon className="w-5 h-5" />
                      </span>
                    )}
                  </>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>
    </div>
  );
};

export default MultiselectDropdown;
